/**
 * 为 designer-editor 的待写入图层准备 editor-next 文本引擎字体子集
 *
 * 字体资源由 FontService 加载，并将引擎字体族映射写回图层 runtime。
 */
import { measureLayer } from '@meitu/saas-editor-core/es/layers/text-layer';
import { setLayerRuntimeValue } from '@meitu/saas-editor-core/es/utils/layer-kit';
import { traverseTrees } from '@meitu/saas-editor-core/es/utils/tree-kit';
import { LayerType, type ILayerProps } from '@meitu/saas-editor-core/es/version-types';
import { editor } from '@/instance';

/**
 * 准备图层在 Canvas 文本引擎中使用的字体子集
 *
 * @param layers - 待处理的图层树
 * @param skipFilter - 是否确认传入数组已经只包含文字图层
 * @returns 字体准备流程完成后的 Promise
 */
export const prepareLayerFontsForEngine = async (layers: ILayerProps[], skipFilter = false): Promise<void> => {
    const textLayers: ILayerProps[] = skipFilter ? layers : [];
    if (!skipFilter) {
        traverseTrees(layers, (layer) => {
            if (layer.layerType === LayerType.TEXT_LAYER) textLayers.push(layer);
        });
    }

    try {
        const requests = textLayers.flatMap((layer) => {
            const infos = editor.services.behavior.get(layer)?.getTextInfo?.(layer);
            if (!Array.isArray(infos) || infos.length === 0) return [];

            const familyChars = new Map<string, string>();
            for (const { fontFamily, text } of infos) {
                if (!fontFamily || !editor.services.font.getByFamily(fontFamily)) continue;
                familyChars.set(fontFamily, `${familyChars.get(fontFamily) || ''}${text || ''}`);
            }

            return [...familyChars].map(([fontFamily, chars]) => ({ layer, fontFamily, chars }));
        });
        const subsetFamilies = await editor.services.font.prepareSubsets(requests);

        subsetFamilies.forEach((subsetFamily, index) => {
            if (!subsetFamily) return;
            const { layer, fontFamily } = requests[index];
            setLayerRuntimeValue(layer, {
                fontFamilyMap: {
                    ...(layer.__runtime?.fontFamilyMap || {}),
                    [fontFamily]: subsetFamily,
                },
            });
        });

        if (!subsetFamilies.every(Boolean)) throw new Error('Failed to prepare designer text layer fonts');
    } catch (error) {
        console.error(error);
    }
};

/**
 * 使用 Canvas 文本引擎测量文字图层
 *
 * @param layer - 待测量文字图层
 * @param fixedWidth - 是否固定图层宽度进行排版
 * @returns 测量后的宽高与最小行内尺寸
 */
export const measureTextLayerByEngine = (layer: ILayerProps, fixedWidth = false) => {
    const engine = editor.services.textEngine.getEngine();
    if (!engine || engine.getRegisteredFontCount() === 0) {
        return {
            width: layer.width,
            height: layer.height,
            minInlineSize: 0,
        };
    }

    return measureLayer(engine, {
        ...layer,
        __fixedWidth: fixedWidth,
    });
};
